import { useCallback, useEffect, useRef, type RefObject } from 'react'

/** Wo die Liste sich festhalten kann — in Dokument-Reihenfolge, also von oben nach unten. */
const ANCHOR_SELECTOR = '[data-month-anchor], [data-date]'

interface Anchor {
  el: HTMLElement
  /** Abstand von der Oberkante des Scrollers, den `el` behalten soll. */
  offset: number
}

interface UseScrollAnchorArgs {
  scrollRef: RefObject<HTMLElement | null>
  /** Der Inhalt im Scroller — wächst er, wird nachgeführt. */
  contentRef: RefObject<HTMLElement | null>
  isActive: boolean
}

export interface ScrollAnchor {
  /** Hält `el` bei `offset` px unter der Oberkante, bis der Nutzer selbst scrollt.
   *  Ohne `offset`: dort, wo es gerade steht. */
  pin: (el: HTMLElement, offset?: number) => void
  /** Rückt den Anker zurück an seine Stelle — nach einem Commit, der ihn verschoben haben kann. */
  restore: () => void
}

const offsetIn = (scrollEl: HTMLElement, el: HTMLElement) =>
  el.getBoundingClientRect().top - scrollEl.getBoundingClientRect().top

/** Das Element, in dem die Oberkante des Scrollers gerade liegt (binäre Suche — die Lagen steigen). */
function elementAtTop(scrollEl: HTMLElement): HTMLElement | null {
  const candidates = scrollEl.querySelectorAll<HTMLElement>(ANCHOR_SELECTOR)
  if (candidates.length === 0) return null
  let lo = 0
  let hi = candidates.length - 1
  let found = -1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (offsetIn(scrollEl, candidates[mid]) <= 0) {
      found = mid
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }
  return candidates[Math.max(found, 0)]
}

/**
 * **Die Liste auf Demnächst bleibt stehen, wenn oberhalb Inhalt wächst.**
 *
 * Monate, deren Daten noch unterwegs sind, stehen erst als kurzer Leer-Zustand
 * da und klappen zur vollen Tagesliste auf, sobald die Abfrage ankommt. Ohne
 * Nachführen schiebt das alles darunter weg — „Heute" landete so im Juni
 * (23.09.2026). Die Seite hat `overflow-anchor: none` (sonst kämpft der
 * Browser mit den Höhen-Animationen), also übernimmt das dieser Hook.
 *
 * Zwei Arten Anker:
 * - **Angeheftet** (`pin`): nach einem Sprung — Laden, „Heute", Monats-Chip.
 *   Gilt, bis der Nutzer selbst scrollt; die Scroll-Ereignisse des Sprungs
 *   selbst ändern ihn nicht.
 * - **Mitlaufend**: danach das Element an der Oberkante, bei jedem Scrollen
 *   neu genommen. Wächst etwas darüber, bleibt stehen, was man sieht.
 */
export function useScrollAnchor({ scrollRef, contentRef, isActive }: UseScrollAnchorArgs): ScrollAnchor {
  const anchorRef = useRef<Anchor | null>(null)
  const pinnedRef = useRef(false)

  const record = useCallback(() => {
    const scrollEl = scrollRef.current
    // Verborgener Tab (`display: none`): Alle Maße sind 0.
    if (!scrollEl || scrollEl.clientHeight === 0) return
    const el = elementAtTop(scrollEl)
    anchorRef.current = el ? { el, offset: offsetIn(scrollEl, el) } : null
  }, [scrollRef])

  const restore = useCallback(() => {
    const scrollEl = scrollRef.current
    const anchor = anchorRef.current
    if (!scrollEl || !anchor || scrollEl.clientHeight === 0) return
    if (!anchor.el.isConnected) {
      pinnedRef.current = false
      record()
      return
    }
    const drift = offsetIn(scrollEl, anchor.el) - anchor.offset
    if (Math.abs(drift) >= 1) scrollEl.scrollTop += drift
  }, [scrollRef, record])

  const pin = useCallback((el: HTMLElement, offset?: number) => {
    const scrollEl = scrollRef.current
    if (!scrollEl) return
    anchorRef.current = { el, offset: offset ?? offsetIn(scrollEl, el) }
    pinnedRef.current = true
  }, [scrollRef])

  useEffect(() => {
    if (!isActive) return
    const scrollEl = scrollRef.current
    const contentEl = contentRef.current
    if (!scrollEl || !contentEl) return

    const onScroll = () => {
      if (!pinnedRef.current) record()
    }
    // Was der Nutzer selbst tut — dann gehört die Stelle ihm.
    const release = () => {
      if (!pinnedRef.current) return
      pinnedRef.current = false
      record()
    }

    scrollEl.addEventListener('scroll', onScroll, { passive: true })
    scrollEl.addEventListener('wheel', release, { passive: true })
    scrollEl.addEventListener('touchstart', release, { passive: true })
    scrollEl.addEventListener('pointerdown', release)
    window.addEventListener('keydown', release)
    // Nach dem Layout, vor dem Malen — der Sprung ist nie zu sehen.
    const ro = new ResizeObserver(restore)
    ro.observe(contentEl)

    return () => {
      scrollEl.removeEventListener('scroll', onScroll)
      scrollEl.removeEventListener('wheel', release)
      scrollEl.removeEventListener('touchstart', release)
      scrollEl.removeEventListener('pointerdown', release)
      window.removeEventListener('keydown', release)
      ro.disconnect()
    }
  }, [isActive, scrollRef, contentRef, record, restore])

  return { pin, restore }
}
